/**
 * Cliente de la API del panel.
 *
 * Fino a propósito: la API devuelve `application/problem+json` (§9) y lo único
 * que hace falta es convertir eso en un error con un mensaje que se pueda
 * enseñar. Una capa de abstracción mayor solo escondería lo que pasa.
 */

import { isProblem, type Problem } from '@digitalshare/contracts';

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? 'http://localhost:3000';

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;

  constructor(message: string, status: number, problem: Problem | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Pista accionable que a veces acompaña al error (§6.5). */
  get hint(): string | null {
    const hint = this.problem?.hint;
    return typeof hint === 'string' ? hint : null;
  }

  get code(): string | null {
    const code = this.problem?.code;
    return typeof code === 'string' ? code : null;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${API_ORIGIN}${path}`, {
      ...init,
      // La sesión va en cookie HttpOnly: sin esto no viaja.
      credentials: 'include',
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('No pudimos contactar con el servidor.', 0, null);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let body: unknown;

  try {
    body = text === '' ? undefined : JSON.parse(text);
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const problem = isProblem(body) ? body : null;
    throw new ApiError(problem?.detail ?? `Error ${response.status}`, response.status, problem);
  }

  return body as T;
}

/**
 * `exactOptionalPropertyTypes` no deja pasar `body: undefined`, así que la
 * propiedad se omite cuando no hay cuerpo en vez de ponerla a undefined.
 */
const withBody = (method: string, body?: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, withBody('POST', body)),
  put: <T>(path: string, body?: unknown) => request<T>(path, withBody('PUT', body)),
  patch: <T>(path: string, body?: unknown) => request<T>(path, withBody('PATCH', body)),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/* -------------------------------------------------------------------------
 * Tipos de la API
 * ---------------------------------------------------------------------- */

export type SiteStatus =
  | 'pending'
  | 'active'
  | 'degraded'
  | 'needs_reauth'
  | 'needs_plugin_update'
  | 'disabled';

export interface Site {
  id: string;
  name: string;
  baseUrl: string;
  status: SiteStatus;
  transport: 'wp_json' | 'rest_route';
  pluginVersion: string | null;
  wpVersion: string | null;
  phpVersion: string | null;
  timezone: string | null;
  seoPlugin: string | null;
  lastHealthAt: string | null;
  lastHealthOk: boolean | null;
  clockSkewSeconds: number | null;
  consecutiveFailures: number;
  lastErrorKind: string | null;
  lastErrorMessage: string | null;
  lastErrorAt: string | null;
  createdAt: string;
}

export interface PairingStart {
  pairing_id: string;
  connect_url: string;
  expires_at: string;
  detected: { pluginVersion: string; protocol: { min: number; max: number }; alreadyPaired: boolean };
  transport: 'wp_json' | 'rest_route';
}

export interface HealthCheckResult {
  ok: boolean;
  latency_ms: number;
  transport?: 'wp_json' | 'rest_route';
  clock_skew_seconds?: number;
  warnings?: string[];
  health?: {
    wp: { version: string; timezone: string; permalinks: string };
    php: { version: string; memory_limit: string };
    seo_plugin: string;
    author: { login: string | null; can_publish: boolean };
  };
  error?: { kind: string; message: string; hint: string | null; retryable: boolean };
}

export const sitesApi = {
  list: () => api.get<{ sites: Site[] }>('/v1/sites'),
  startPairing: (siteUrl: string, name?: string) =>
    api.post<PairingStart>('/v1/sites/pairing', { siteUrl, ...(name ? { name } : {}) }),
  healthCheck: (id: string) => api.post<HealthCheckResult>(`/v1/sites/${id}/health-check`, {}),
  disconnect: (id: string) => api.delete<{ disconnected: boolean }>(`/v1/sites/${id}`),
};

/* -------------------------------------------------------------------------
 * Secciones y mapeo
 * ---------------------------------------------------------------------- */

export interface Section {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface SiteCategory {
  wpTermId: number;
  name: string;
  slug: string;
  parentWpId: number;
  postCount: number;
}

export interface SiteMapping {
  siteId: string;
  siteName: string;
  categories: SiteCategory[];
  categoriesFetchedAt: string | null;
  mappings: Array<{
    sectionId: string;
    wpTermId: number;
    wpTermName: string | null;
    /** La categoría mapeada ya no existe en el sitio. */
    stale: boolean;
  }>;
  missingSectionIds: string[];
}

export interface SiteGroup {
  id: string;
  name: string;
  siteIds: string[];
  createdAt: string;
}

export const sectionsApi = {
  list: () => api.get<{ sections: Section[] }>('/v1/sections'),

  /**
   * Qué sitios tienen mapeada cada seccion, por id.
   *
   * Una sola peticion en vez de una por sitio: con veinte periodicos, pedir el
   * mapeo de cada uno cada vez que cambia el desplegable de seccion haria la
   * pantalla inservible.
   */
  coverage: () => api.get<{ coverage: Record<string, string[]> }>('/v1/sections/coverage'),
  create: (name: string) => api.post<Section>('/v1/sections', { name }),
  rename: (id: string, name: string) => api.put<Section>(`/v1/sections/${id}`, { name }),
  remove: (id: string) => api.delete<{ deleted: boolean; mappingsRemoved: number }>(`/v1/sections/${id}`),
};

export const mappingApi = {
  get: (siteId: string) => api.get<SiteMapping>(`/v1/sites/${siteId}/section-map`),
  save: (siteId: string, mappings: Array<{ sectionId: string; wpTermId: number | null }>) =>
    api.put<{ saved: number; removed: number }>(`/v1/sites/${siteId}/section-map`, { mappings }),
};

export const groupsApi = {
  list: () => api.get<{ groups: SiteGroup[] }>('/v1/site-groups'),
  create: (name: string, siteIds: string[]) => api.post<SiteGroup>('/v1/site-groups', { name, siteIds }),
  update: (id: string, name: string, siteIds: string[]) =>
    api.put<SiteGroup>(`/v1/site-groups/${id}`, { name, siteIds }),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/v1/site-groups/${id}`),
};

export const siteSecretApi = {
  rotate: (siteId: string) =>
    api.post<{ rotated: boolean; error?: { kind: string; message: string; hint: string | null } }>(
      `/v1/sites/${siteId}/rotate-secret`,
      {},
    ),
};

/* -------------------------------------------------------------------------
 * Medios
 * ---------------------------------------------------------------------- */

export interface MediaAsset {
  id: string;
  originalFilename: string;
  storageKey: string;
  sha256: string;
  mime: string;
  bytes: number;
  width: number;
  height: number;
  alt: string | null;
  caption: string | null;
  createdAt: string;
  url: string;
  reused?: boolean;
}

export const mediaApi = {
  list: () => api.get<{ assets: MediaAsset[] }>('/v1/media'),

  /**
   * Sube una imagen.
   *
   * No pasa por `api.post` porque ahí se fija `Content-Type: application/json`,
   * y con `FormData` hay que dejar que el navegador ponga el suyo con su
   * `boundary`. Forzar JSON aquí hace que el servidor no encuentre el archivo.
   */
  async upload(file: File, alt?: string): Promise<MediaAsset> {
    const form = new FormData();
    form.set('file', file);
    if (alt) {
      form.set('alt', alt);
    }

    const response = await fetch(`${API_ORIGIN}/v1/media`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });

    const body: unknown = await response.json().catch(() => undefined);

    if (!response.ok) {
      const problem = isProblem(body) ? body : null;
      throw new ApiError(problem?.detail ?? 'No pudimos subir la imagen.', response.status, problem);
    }

    return body as MediaAsset;
  },
};

/* -------------------------------------------------------------------------
 * Artículos
 * ---------------------------------------------------------------------- */

export type ArticleStatus = 'draft' | 'in_review' | 'approved' | 'archived';

export interface ArticleSummary {
  id: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  status: ArticleStatus;
  sectionId: string | null;
  featuredMediaId: string | null;
  updatedAt: string;
  createdAt: string;
}

export interface ArticleOverride {
  siteId: string;
  title: string | null;
  slug: string | null;
  excerpt: string | null;
  contentHtml: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  canonicalMode: 'none' | 'origin' | 'noindex' | null;
}

export interface Article extends ArticleSummary {
  contentHtml: string;
  contentJson: Record<string, unknown> | null;
  tags: string[] | null;
  seoTitle: string | null;
  seoDescription: string | null;
  overrides: ArticleOverride[];
}

export interface ArticleWrite {
  title: string;
  slug?: string;
  excerpt?: string | null | undefined;
  contentHtml?: string;
  featuredMediaId?: string | null;
  sectionId?: string | null;
  tags?: string[];
  seoTitle?: string | null;
  seoDescription?: string | null;
  status?: ArticleStatus;
}

export const articlesApi = {
  list: () => api.get<{ articles: ArticleSummary[] }>('/v1/articles'),
  get: (id: string) => api.get<Article>(`/v1/articles/${id}`),
  create: (body: ArticleWrite) => api.post<{ id: string; createdAt: string }>('/v1/articles', body),
  update: (id: string, body: Partial<ArticleWrite>) =>
    api.put<{ id: string; updatedAt: string }>(`/v1/articles/${id}`, body),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/v1/articles/${id}`),
  saveOverride: (id: string, siteId: string, body: Partial<ArticleOverride>) =>
    api.put<{ id: string }>(`/v1/articles/${id}/overrides/${siteId}`, body),
};

/* -------------------------------------------------------------------------
 * Publicaciones
 * ---------------------------------------------------------------------- */

export type TargetStatus =
  | 'pending'
  | 'preparing'
  | 'prepared'
  | 'publishing'
  | 'published'
  | 'failed_retrying'
  | 'failed'
  | 'canceled'
  | 'skipped';

export type PublicationStatus = 'scheduled' | 'processing' | 'completed' | 'partial' | 'failed' | 'canceled';

export interface PublicationTarget {
  id: string;
  siteId: string;
  siteName: string;
  status: TargetStatus;
  remoteUrl: string | null;
  attemptCount: number;
  nextAttemptAt: string | null;
  publishedAt: string | null;
  lastErrorKind: string | null;
  lastErrorMessage: string | null;
  lastErrorHint: string | null;
}

export interface PublicationSummary {
  id: string;
  articleId: string;
  articleTitle: string;
  mode: 'now' | 'scheduled';
  status: PublicationStatus;
  scheduledAt: string | null;
  tzDisplay: string | null;
  createdAt: string;
}

export interface Publication extends PublicationSummary {
  targets: PublicationTarget[];
}

export interface TargetEvent {
  id: string;
  kind: string;
  httpStatus: number | null;
  errorKind: string | null;
  message: string | null;
  durationMs: number | null;
  detail: Record<string, unknown> | null;
  at: string;
}

export interface PublicationCreate {
  articleId: string;
  siteIds?: string[];
  groupIds?: string[];
  mode: 'now' | 'scheduled';
  scheduledAt?: string;
  tz?: string;
}

export const publicationsApi = {
  list: () => api.get<{ publications: PublicationSummary[] }>('/v1/publications'),
  get: (id: string) => api.get<Publication>(`/v1/publications/${id}`),
  create: (body: PublicationCreate) => api.post<Publication>('/v1/publications', body),
  cancel: (id: string) => api.post<{ canceled: number; status: string }>(`/v1/publications/${id}/cancel`, {}),
  retryTarget: (publicationId: string, targetId: string) =>
    api.post<{ queued: boolean }>(`/v1/publications/${publicationId}/targets/${targetId}/retry`, {}),
  events: (publicationId: string, targetId: string) =>
    api.get<{ events: TargetEvent[] }>(`/v1/publications/${publicationId}/targets/${targetId}/events`),

  /** URL del flujo de estado en vivo. La abre `EventSource` en la pantalla. */
  liveUrl: (id: string) => `${API_ORIGIN}/v1/publications/${id}/live`,
};

/* -------------------------------------------------------------------------
 * Acceso: configuración pública, invitaciones y límites
 * ---------------------------------------------------------------------- */

export const configApi = {
  get: () => api.get<{ registrationMode: 'open' | 'invitation' }>('/v1/config'),
};

export interface InvitationPreview {
  email: string;
  role: string | null;
  organizationName: string;
}

export const invitationsApi = {
  /** Sin sesión: quien abre el enlace del correo todavía no tiene cuenta. */
  preview: (id: string) => api.get<InvitationPreview>(`/v1/invitations/${id}`),
};

export interface OrgLimitsView {
  limits: { maxSites: number; maxMembers: number };
  usage: { sites: number; members: number; pendingInvitations: number };
  suspended: boolean;
}

export const orgApi = {
  limits: () => api.get<OrgLimitsView>('/v1/org/limits'),
};

/* -------------------------------------------------------------------------
 * Administración de la plataforma
 * ---------------------------------------------------------------------- */

export interface PlatformClient {
  id: string;
  name: string;
  createdAt: string;
  owners: Array<{ name: string; email: string }>;
  pendingOwnerInvitation: { email: string; expiresAt: string; expired: boolean } | null;
  limits: { maxSites: number; maxMembers: number };
  usage: { sites: number; members: number; pendingInvitations: number; publicationsLast30Days: number };
  lastPublicationAt: string | null;
  suspended: boolean;
  paused: boolean;
}

export const platformApi = {
  me: () => api.get<{ isPlatformAdmin: boolean }>('/v1/platform/me'),
  clients: () => api.get<{ clients: PlatformClient[] }>('/v1/platform/clients'),
  create: (body: { name: string; ownerEmail: string; maxSites: number; maxMembers: number }) =>
    api.post<{ id: string; invitationId: string }>('/v1/platform/clients', body),
  update: (id: string, body: { maxSites?: number; maxMembers?: number; suspended?: boolean }) =>
    api.patch<{ updated: boolean }>(`/v1/platform/clients/${id}`, body),
  resendInvitation: (id: string) =>
    api.post<{ email: string }>(`/v1/platform/clients/${id}/resend-invitation`, {}),
};
